import type { AnyRouter } from '@tanstack/react-router'

import { MinutesToMilliseconds } from 'Common/utils/helpers/HelperTime'

const checkIntervalMs = MinutesToMilliseconds(5)
const reloadGuardKey = 'app_chunk_reload_at'
const reloadGuardMs = 10_000

let newVersionAvailable = false
let activeRouter: AnyRouter | undefined
let routePreloads = 0

const checkForNewVersion = async () => {
    if (newVersionAvailable) return
    try {
        const response = await fetch('/version.json', { cache: 'no-store' })
        if (!response.ok) return
        const { buildId } = (await response.json()) as { buildId?: string }
        newVersionAvailable = Boolean(buildId) && buildId !== __APP_BUILD_ID__
    } catch {
        return
    }
}

const failedDuringHoverPreload = () => routePreloads > 0 && !activeRouter?.state.isLoading

const reloadedRecently = () => {
    try {
        return Date.now() - Number(sessionStorage.getItem(reloadGuardKey) ?? 0) < reloadGuardMs
    } catch {
        return true
    }
}

const markReload = () => {
    try {
        sessionStorage.setItem(reloadGuardKey, String(Date.now()))
    } catch {
        return
    }
}

export function reloadOnFailedChunk() {
    window.addEventListener('vite:preloadError', (event) => {
        if (failedDuringHoverPreload()) {
            newVersionAvailable = true
            return
        }
        if (reloadedRecently()) return
        event.preventDefault()
        markReload()
        window.location.reload()
    })
}

export function watchForNewVersion(router: AnyRouter) {
    if (import.meta.env.DEV) return

    activeRouter = router
    const preloadRoute = router.preloadRoute
    router.preloadRoute = (async (options) => {
        routePreloads += 1
        try {
            return await preloadRoute(options)
        } finally {
            routePreloads -= 1
        }
    }) as typeof router.preloadRoute

    window.setInterval(checkForNewVersion, checkIntervalMs)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkForNewVersion()
    })

    router.subscribe('onBeforeNavigate', ({ fromLocation, toLocation, pathChanged }) => {
        if (newVersionAvailable && fromLocation && pathChanged) {
            window.location.assign(toLocation.publicHref)
        }
    })
}
